import { useLoadProjectArea } from "@/components/common/project-provider/project-loading-context";
import {
  isLightweightGenericSheet,
  isLightweightImg2d,
  isLightweightImg360,
} from "@/store/lightweight-elements-discriminators";
import {
  selectAncestorArea,
  selectLightweightElement,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import type { LightweightElementsStore } from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector } from "@/store/store-hooks";
import type { GUID } from "@faro-lotv/foundation";
import { createSelector } from "@reduxjs/toolkit";

/**
 * @returns whether the element is navigable in walk mode and whether its area is still loading
 * @param elementId the ID of the element whose area to load
 */
export function useLoadAreaIfNecessary(elementId: GUID): {
  /** whether the element can be rendered in walk mode */
  navigable: boolean;
  /** whether the element's area is still being fetched (only meaningful when `navigable` is `true`) */
  loading: boolean;
} {
  const isRenderable = useAppSelector(selectIsElementWalkRenderable(elementId));

  const area = useAppSelector(
    selectAncestorArea(isRenderable ? elementId : undefined),
  );

  // Load the whole area containing the element so the Viewer can render it.
  const areaLoading = useLoadProjectArea(isRenderable ? area?.id : undefined);

  return {
    navigable: isRenderable,
    loading: isRenderable ? areaLoading : false,
  };
}

/**
 * @returns true if the given element can be rendered as the active element in walk mode, based on its element type.
 *   Operates on lightweight elements so that elements not yet fully loaded into the iElements store can still be evaluated.
 * @param state the current app state
 * @param id the ID of the element to check
 */
const selectIsElementWalkRenderable = curryAppSelector(
  createSelector(
    [
      selectLightweightElementsSlice,
      (state: LightweightElementsStore, id: GUID | undefined) => id,
    ],
    (lightweightElements, id): boolean => {
      if (!id) return false;

      const element = selectLightweightElement(id)({ lightweightElements });
      if (!element) return false;

      return (
        isLightweightImg360(element) ||
        isLightweightImg2d(element) ||
        isLightweightGenericSheet(element)
      );
    },
  ),
);
